/**
 * Expansion von Wiederholungsregeln.
 *
 * DIES IST DIE HEIKELSTE DATEI DES PROJEKTS. Bitte vor Aenderungen lesen.
 *
 * Das Problem: Eine Serie "jeden Montag 18:00" muss ueber den Wechsel von
 * Sommer- auf Winterzeit hinweg um 18:00 bleiben. Wuerde man Startzeiten als
 * echte Zeitpunkte (UTC-Instants) speichern und `rrule` darauf loslassen,
 * wanderte die Uhrzeit an der Umstellung um eine Stunde.
 *
 * Die Loesung: Wir rechnen in "floating space". `rrule` bekommt Daten, deren
 * UTC-Felder unsere lokalen Wanduhr-Felder tragen, und wir lesen die
 * Ergebnisse mit `getUTC*` wieder als Wanduhrzeit aus. Damit kommt in der
 * gesamten Expansion nie ein Zeitzonen-Offset vor - und das Ergebnis ist
 * unabhaengig davon, in welcher Zeitzone der Browser laeuft.
 *
 * Die Umrechnung ist hier gekapselt: `toFloatingUtc` und `fromFloatingUtc`
 * sind die einzigen beiden Stellen, an denen ein `Date` die Grenze ueberquert.
 */
import { RRule, rrulestr } from 'rrule';

import type {
  CalendarEvent, CalendarOccurrence, LocalDate, LocalDateTime,
} from '@/domain/types';
import {
  addDays, datePart, differenceInMinutes, minutesOfDay, atMinutes,
} from '@/lib/datetime';

/**
 * Obergrenze pro Serie und Abfrage. Eine kaputte oder absurd dichte Regel darf
 * die Oberflaeche nicht einfrieren - lieber unvollstaendig als haengend.
 */
export const MAX_OCCURRENCES_PER_SERIES = 2000;

// ---------------------------------------------------------------------------
// Die Grenze zwischen floating-Strings und rrule
// ---------------------------------------------------------------------------

/** `'2026-09-16T18:00'` -> `Date`, dessen UTC-Felder 2026-09-16 18:00 tragen. */
export function toFloatingUtc(value: LocalDateTime | LocalDate): Date {
  const y = +value.slice(0, 4);
  const m = +value.slice(5, 7);
  const d = +value.slice(8, 10);
  const hh = value.length > 10 ? +value.slice(11, 13) : 0;
  const mm = value.length > 10 ? +value.slice(14, 16) : 0;
  return new Date(Date.UTC(y, m - 1, d, hh, mm, 0, 0));
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Gegenrichtung: UTC-Felder eines `Date` als lokale Wanduhrzeit lesen. */
export function fromFloatingUtc(date: Date): LocalDateTime {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
    + `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

/**
 * Baut das RRule-Objekt einer Serie.
 *
 * `rrule` selbst rechnet intern mit UTC - genau deshalb funktioniert der Trick:
 * wir fuettern es mit floating-Feldern, es rechnet offsetfrei, und wir lesen
 * floating-Felder zurueck.
 */
function buildRule(event: CalendarEvent): RRule | null {
  if (!event.rrule) return null;
  try {
    const dtstart = toFloatingUtc(event.start);
    const rule = rrulestr(
      event.rrule.startsWith('RRULE:') ? event.rrule : `RRULE:${event.rrule}`,
      { dtstart },
    );
    return rule instanceof RRule ? rule : null;
  } catch {
    // Eine unlesbare Regel darf den ganzen Kalender nicht sprengen: der Termin
    // erscheint dann eben nur als Einzeltermin an seinem Starttag.
    return null;
  }
}

/** Menschenlesbare Pruefung, ob eine Regel ueberhaupt verwendbar ist. */
export function isValidRrule(rule: string): boolean {
  try {
    const parsed = rrulestr(rule.startsWith('RRULE:') ? rule : `RRULE:${rule}`, {
      dtstart: new Date(Date.UTC(2026, 0, 1)),
    });
    return parsed instanceof RRule;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Expansion
// ---------------------------------------------------------------------------

export interface Range {
  /** einschliesslich */
  from: LocalDateTime;
  /** ausschliesslich */
  to: LocalDateTime;
}

/** Dauer eines Termins in Minuten - bei ganztaegigen in ganzen Tagen. */
export function eventDurationMinutes(event: CalendarEvent): number {
  if (event.allDay) {
    const days = Math.max(
      1,
      Math.round(
        (toFloatingUtc(event.end).getTime() - toFloatingUtc(event.start).getTime()) / 86_400_000,
      ),
    );
    return days * 1440;
  }
  return Math.max(1, differenceInMinutes(event.start, event.end));
}

function occurrenceFrom(
  event: CalendarEvent,
  start: LocalDateTime,
  durationMinutes: number,
  opts: { isRecurring: boolean; isOverride: boolean; originalStart?: LocalDateTime },
): CalendarOccurrence {
  const end = event.allDay
    ? addDays(datePart(start), Math.round(durationMinutes / 1440))
    : addMinutesFloating(start, durationMinutes);

  return {
    occurrenceId: `${event.id}::${opts.originalStart ?? start}`,
    eventId: event.id,
    ...(opts.originalStart ? { seriesId: event.recurringEventId ?? event.id } : {}),
    isRecurring: opts.isRecurring,
    isOverride: opts.isOverride,
    originalStart: opts.originalStart,
    start: event.allDay ? datePart(start) : start,
    end,
    allDay: event.allDay,
    title: event.title,
    areaId: event.areaId,
    sourceTaskId: event.sourceTaskId,
    training: event.training,
    vorlage: event.vorlage,
    subjectId: event.subjectId,
    projectId: event.projectId,
    taskIds: event.taskIds,
    reminderMinutes: event.reminderMinutes,
    location: event.location,
    // Markierungen (D-03) - bei Ausnahmen die der Ausnahmezeile.
    categoryId: event.categoryId,
    priority: event.priority,
    markFill: event.markFill,
    markBorder: event.markBorder,
    markDot: event.markDot,
  };
}

/** Minutenaddition ohne Umweg ueber die lokale Zeitzone. */
function addMinutesFloating(value: LocalDateTime, minutes: number): LocalDateTime {
  const total = minutesOfDay(value) + minutes;
  const dayShift = Math.floor(total / 1440);
  const rest = ((total % 1440) + 1440) % 1440;
  return atMinutes(addDays(datePart(value), dayShift), rest);
}

/** Ueberlappt [aStart, aEnd) den Bereich [from, to)? */
function overlaps(aStart: string, aEnd: string, range: Range): boolean {
  return aStart < range.to && aEnd > range.from;
}

/**
 * Expandiert eine Serie im gegebenen Bereich.
 *
 * `overrides` sind die Ausnahmezeilen dieser Serie; sie ersetzen das Vorkommen
 * mit passendem `originalStart` und werden hier nicht selbst zurueckgegeben -
 * der Aufrufer mischt sie ein, weil er sie ohnehin schon geladen hat.
 */
export function expandSeries(
  master: CalendarEvent,
  range: Range,
  exdates: Set<string> = new Set(master.exdates ?? []),
): CalendarOccurrence[] {
  const rule = buildRule(master);
  const duration = eventDurationMinutes(master);

  if (!rule) {
    // Keine oder kaputte Regel: der Termin ist ein Einzeltermin.
    const occ = occurrenceFrom(master, normalizeStart(master), duration, {
      isRecurring: false, isOverride: false,
    });
    return overlaps(String(occ.start), String(occ.end), range) ? [occ] : [];
  }

  // Ein Vorkommen, das VOR dem Bereich beginnt, kann noch hineinragen. Deshalb
  // wird das Fenster um die Dauer nach hinten erweitert.
  const searchFrom = toFloatingUtc(addMinutesFloating(range.from, -duration));
  const searchTo = toFloatingUtc(range.to);

  let starts: Date[];
  try {
    starts = rule.between(searchFrom, searchTo, true);
  } catch {
    return [];
  }

  if (starts.length > MAX_OCCURRENCES_PER_SERIES) {
    starts = starts.slice(0, MAX_OCCURRENCES_PER_SERIES);
  }

  const result: CalendarOccurrence[] = [];
  for (const date of starts) {
    const start = fromFloatingUtc(date);
    if (exdates.has(start)) continue;

    const occ = occurrenceFrom(master, start, duration, {
      isRecurring: true, isOverride: false, originalStart: start,
    });
    if (overlaps(String(occ.start), String(occ.end), range)) {
      result.push(occ);
    }
  }
  return result;
}

/** Ganztaegige Termine werden intern auf Mitternacht normalisiert. */
function normalizeStart(event: CalendarEvent): LocalDateTime {
  return event.allDay ? `${datePart(event.start)}T00:00` : (event.start as LocalDateTime);
}

/**
 * Baut aus allen betroffenen Zeilen die Vorkommen eines Zeitraums.
 *
 * `events` enthaelt beliebig gemischt Einzeltermine, Serien und Ausnahmen -
 * genau so, wie sie aus der Datenbank kommen.
 */
export function expandEvents(events: CalendarEvent[], range: Range): CalendarOccurrence[] {
  const masters: CalendarEvent[] = [];
  const singles: CalendarEvent[] = [];
  /** Ausnahmen, gruppiert nach der Serie, zu der sie gehoeren. */
  const overridesBySeries = new Map<string, CalendarEvent[]>();

  for (const event of events) {
    if (event.status === 'cancelled') continue;

    if (event.recurringEventId) {
      const list = overridesBySeries.get(event.recurringEventId) ?? [];
      list.push(event);
      overridesBySeries.set(event.recurringEventId, list);
    } else if (event.rrule) {
      masters.push(event);
    } else {
      singles.push(event);
    }
  }

  const result: CalendarOccurrence[] = [];

  for (const event of singles) {
    const occ = occurrenceFrom(event, normalizeStart(event), eventDurationMinutes(event), {
      isRecurring: false, isOverride: false,
    });
    if (overlaps(String(occ.start), String(occ.end), range)) result.push(occ);
  }

  for (const master of masters) {
    const overrides = overridesBySeries.get(master.id) ?? [];

    // Ein ueberschriebenes Vorkommen darf nicht zusaetzlich als regulaeres
    // Vorkommen erscheinen - unabhaengig davon, ob `exdates` gepflegt wurde.
    const blocked = new Set([
      ...(master.exdates ?? []),
      ...overrides.map((o) => o.originalStart).filter((s): s is string => Boolean(s)),
    ]);

    result.push(...expandSeries(master, range, blocked));

    for (const override of overrides) {
      const occ = occurrenceFrom(
        override,
        normalizeStart(override),
        eventDurationMinutes(override),
        { isRecurring: true, isOverride: true, originalStart: override.originalStart },
      );
      if (overlaps(String(occ.start), String(occ.end), range)) result.push(occ);
    }
  }

  // Verwaiste Ausnahmen (Serie geloescht) trotzdem anzeigen - lieber ein
  // Termin zu viel als ein stillschweigend verschwundener.
  for (const [seriesId, overrides] of overridesBySeries) {
    if (masters.some((m) => m.id === seriesId)) continue;
    for (const override of overrides) {
      const occ = occurrenceFrom(
        override, normalizeStart(override), eventDurationMinutes(override),
        { isRecurring: false, isOverride: true, originalStart: override.originalStart },
      );
      if (overlaps(String(occ.start), String(occ.end), range)) result.push(occ);
    }
  }

  result.sort((a, b) => String(a.start).localeCompare(String(b.start)));
  return result;
}
